﻿
import { FC } from 'react';
import { Container, Section, Card, Badge } from '../components/ui';
import { SEOHead } from '../components/seo';
import { siteConfig } from '../config/site';
import { PageProps } from './HomePage';

export const PrivacyPolicyPage: FC<PageProps> = () => {
  return (
    <>
      <SEOHead
        title="Privacy Policy — Connect Source"
        description="Read how Connect Source collects, uses, protects, and retains personal information when you use our website, digital products, and services."
      />

      <Section
        style={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid var(--color-border-subtle)',
          padding: '4rem 0'
        }}
      >
        <Container size="md">
          <Badge variant="neutral" style={{ marginBottom: '1rem' }}>
            Legal & Compliance
          </Badge>

          <h1
            style={{
              fontSize: '2.5rem',
              fontWeight: 800,
              color: 'var(--color-navy-dark)',
              lineHeight: 1.2
            }}
          >
            Privacy Policy
          </h1>

          <p
            style={{
              fontSize: '0.9rem',
              color: 'var(--color-text-muted)',
              marginTop: '0.5rem'
            }}
          >
            Last Updated: October 2026 • Connect Source
          </p>

          <Card
            style={{
              marginTop: '2.5rem',
              padding: '2.5rem',
              lineHeight: 1.75,
              color: 'var(--color-text-secondary)',
              fontSize: '0.95rem'
            }}
          >
            <h2>1. Who We Are</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              Connect Source is a business operated as a sole proprietorship
              in India. We provide career coaching, HR and business support
              services, and digital career resources. For privacy questions
              or requests relating to your personal information, contact us
              using the details provided at the end of this policy.
            </p>

            <h2>2. Information We Collect</h2>
            <p style={{ marginBottom: '0.75rem' }}>
              Depending on how you use our website and services, we may
              collect information you provide directly, information related
              to your purchases, and limited technical information.
            </p>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1.5rem' }}>
              <li>
                <strong>Contact details:</strong> Name, email address, and
                telephone or WhatsApp number submitted through forms,
                consultations, or checkout.
              </li>
              <li>
                <strong>Professional information:</strong> Career history,
                skills, target roles, resume details, and information you
                choose to share during coaching or consultation.
              </li>
              <li>
                <strong>Order information:</strong> Products purchased,
                order references, payment status, and information needed to
                provide customer support. Payment providers may process
                payment details under their own applicable terms.
              </li>
              <li>
                <strong>Technical information:</strong> Limited device,
                browser, diagnostic, and website-usage information where
                collected by our systems or enabled services.
              </li>
            </ul>

            <h2>3. How We Use Information</h2>
            <p style={{ marginBottom: '0.75rem' }}>
              We use information only for legitimate business purposes
              connected with our website and services, including:
            </p>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1.5rem' }}>
              <li>Responding to enquiries and consultation requests.</li>
              <li>Delivering free resources and purchased digital products.</li>
              <li>Processing and verifying orders and payment status.</li>
              <li>Providing coaching, HR, and business support services.</li>
              <li>Sending transactional emails and essential service updates.</li>
              <li>Maintaining website security, preventing misuse, and resolving technical problems.</li>
              <li>Meeting applicable legal, accounting, and regulatory obligations.</li>
            </ul>

            <h2>4. Sharing and Service Providers</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              We do not sell personal information to data brokers. Where
              necessary to operate our services, information may be processed
              by service providers involved in payment processing, website
              hosting, data storage, email delivery, or technical operations.
              These providers may process information under their own terms
              and applicable privacy and security obligations. We may also
              disclose information where required by law or necessary to
              protect our legal rights, users, or services.
            </p>

            <h2>5. Payments and Financial Information</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              Payments are handled through the payment provider presented
              during checkout. We do not intend to collect or store complete
              card numbers, card security codes, banking passwords, PINs, or
              one-time passwords on our website servers. Payment information
              is subject to the payment provider's processing practices and
              applicable terms. Please do not send sensitive payment
              credentials to us by email or WhatsApp.
            </p>

            <h2>6. Email and Communications</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              We may use the email address you provide to deliver a resource,
              confirm an order, provide download access, respond to support
              requests, or communicate important service information.
              Promotional communications, where sent, should respect
              applicable consent and opt-out requirements.
            </p>

            <h2>7. Data Retention and Deletion Requests</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              We retain information for as long as reasonably necessary to
              provide services, maintain order and download records, resolve
              disputes, and comply with applicable legal obligations.
              Retention periods may vary by record type. You may contact us
              to request access to, correction of, or deletion of personal
              information, subject to applicable law and records we are
              legally required or permitted to retain.
            </p>

            <h2>8. Data Security</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              We use technical and organisational measures intended to
              protect information against unauthorised access, loss, misuse,
              or disclosure. No internet transmission or storage system can
              be guaranteed completely secure. Please contact us promptly
              if you believe information associated with your account or
              order has been exposed or misused.
            </p>

            <h2>9. International Processing</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              Some service providers may process or store information in
              countries other than your own. Where this occurs, information
              may be subject to the laws applicable in those locations.
              We will handle personal information in accordance with
              applicable legal requirements.
            </p>

            <h2>10. Changes to This Policy</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              We may update this policy when our services, systems, or
              legal obligations change. The updated date at the top of this
              page indicates when the policy was most recently revised.
            </p>

            <h2>11. Contact and Privacy Requests</h2>
            <p>
              For privacy questions, data requests, or concerns about how
              information is handled, contact Connect Source at{' '}
              <a
                href={`mailto:${siteConfig.contactEmail}`}
                style={{ color: 'var(--color-brand-primary)' }}
              >
                {siteConfig.contactEmail}
              </a>
              . Please include enough information for us to understand and
              respond to your request, but do not send passwords, payment
              credentials, or other unnecessary sensitive information.
            </p>
          </Card>
        </Container>
      </Section>
    </>
  );
};
